import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import Tabs, { type TabItem } from '@/components/base/Tabs';
import EmptyState from '@/components/base/EmptyState';
import Button from '@/components/base/Button';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import Toast, { type ToastType } from '@/components/base/Toast';
import OrdersFilters from '@/pages/orders/components/OrdersFilters';
import OrdersTable from '@/pages/orders/components/OrdersTable';
import { type OrderStatus } from '@/mocks/orders';
import { emptyFilters, type OrderFilters, type OrderActionHandlers } from '@/pages/orders/types';
import { useAppData } from '@/store/AppDataContext';

const tabStatuses: Record<string, OrderStatus[]> = {
  pending: ['pending'],
  active: ['accepted', 'processing', 'ready', 'needs_driver'],
  completed: ['completed'],
  cancelled: ['cancelled', 'rejected'],
};

const emptyMessages: Record<string, { title: string; message: string; icon: string }> = {
  pending: { title: 'لا توجد طلبات جديدة', message: 'الطلبات الجديدة هتظهر هنا أول ما توصل.', icon: 'ri-inbox-archive-line' },
  active: { title: 'لا توجد طلبات جارية', message: 'لا توجد طلبات قيد التنفيذ حالياً.', icon: 'ri-loader-4-line' },
  completed: { title: 'لا توجد طلبات مكتملة', message: 'الطلبات اللي تم تسليمها للعملاء بتظهر هنا.', icon: 'ri-checkbox-circle-line' },
  cancelled: { title: 'لا توجد طلبات ملغاة', message: 'الطلبات المرفوضة أو الملغاة بتظهر هنا.', icon: 'ri-close-circle-line' },
};

interface ToastState { type: ToastType; message: string }

export default function OrdersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { orders, patchOrderStatus } = useAppData();

  const [activeTab, setActiveTab] = useState('pending');
  const [filters, setFilters] = useState<OrderFilters>(emptyFilters);
  const [loading, setLoading] = useState(true);
  const [flashId, setFlashId] = useState('');
  const [rejectOrderId, setRejectOrderId] = useState('');
  const [toast, setToast] = useState<ToastState | null>(null);

  useEffect(() => {
    const state = location.state as { toast?: ToastState } | null;
    if (state?.toast) {
      setToast(state.toast);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, [activeTab]);

  const tabs: TabItem[] = [
    { id: 'pending', label: 'جديدة', count: orders.filter((o) => o.status === 'pending').length },
    { id: 'active', label: 'جارية', count: orders.filter((o) => tabStatuses.active.includes(o.status)).length },
    { id: 'completed', label: 'مكتملة', count: orders.filter((o) => o.status === 'completed').length },
    { id: 'cancelled', label: 'ملغاة', count: orders.filter((o) => tabStatuses.cancelled.includes(o.status)).length },
  ];

  const filtered = useMemo(() => {
    const statuses = tabStatuses[activeTab] || [];
    return orders.filter((order) => {
      if (!statuses.includes(order.status)) return false;
      if (filters.priority && order.priority !== filters.priority) return false;
      if (filters.delivery && order.deliveryMethod !== filters.delivery) return false;
      if (filters.source && order.source !== filters.source) return false;
      if (filters.search && !order.orderNo.includes(filters.search.trim())) return false;
      if (filters.range.from && order.date < filters.range.from) return false;
      if (filters.range.to && order.date > filters.range.to) return false;
      return true;
    });
  }, [orders, activeTab, filters]);

  const flash = (id: string) => {
    setFlashId(id);
    setTimeout(() => setFlashId(''), 1200);
  };

  const handlers: OrderActionHandlers = {
    onAccept: (id) => {
      patchOrderStatus(id, 'accepted', 'تم قبول الطلب');
      flash(id);
      setToast({ type: 'success', message: 'تم قبول الطلب وبدأ تجهيزه للاستلام' });
    },
    onReject: (id) => setRejectOrderId(id),
    onStart: (id) => {
      patchOrderStatus(id, 'processing', 'بدأ التنفيذ في المغسلة');
      flash(id);
      setToast({ type: 'info', message: 'تم تحديث الطلب إلى قيد التنفيذ' });
    },
    onReady: (id) => {
      patchOrderStatus(id, 'ready', 'جاهز للتسليم');
      flash(id);
      setToast({ type: 'success', message: 'تم تعليم الطلب كجاهز للتسليم' });
    },
    onComplete: (id) => {
      patchOrderStatus(id, 'completed', 'تم تسليم الطلب للعميل');
      flash(id);
      setToast({ type: 'success', message: 'تم تأكيد تسليم الطلب بنجاح' });
    },
    onAssign: (id) => {
      const order = orders.find((o) => o.id === id);
      if (!order) return;
      navigate(`/orders/assign?orderId=${order.id}&orderNo=${encodeURIComponent(order.orderNo)}`);
    },
  };

  const confirmReject = () => {
    patchOrderStatus(rejectOrderId, 'rejected', 'تم رفض الطلب وإعادة المبلغ');
    flash(rejectOrderId);
    setRejectOrderId('');
    setToast({ type: 'info', message: 'تم رفض الطلب وإعادة المبلغ للعميل' });
  };

  const tableKey = `${activeTab}-${JSON.stringify(filters)}`;

  return (
    <DashboardLayout title="إدارة الطلبات">
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <h2 className="font-bold text-[18px] text-foreground-950">إدارة الطلبات</h2>
          <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-primary-100 px-2 text-[12px] font-semibold text-primary-700">
            {orders.length}
          </span>
        </div>

        <div className="rounded-card border border-background-200 bg-white shadow-card">
          <div className="px-2 pt-1">
            <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} />
          </div>

          <div className="border-b border-background-100 p-4">
            <OrdersFilters filters={filters} onChange={setFilters} onClear={() => setFilters(emptyFilters)} />
          </div>

          <div className="p-4">
            {loading ? (
              <OrdersTable orders={[]} loading flashId="" onOpenOrder={() => undefined} {...handlers} />
            ) : filtered.length === 0 ? (
              <EmptyState
                icon={emptyMessages[activeTab].icon}
                title={emptyMessages[activeTab].title}
                message={emptyMessages[activeTab].message}
                action={
                  <Button variant="outline" size="md" icon="ri-refresh-line" onClick={() => setFilters(emptyFilters)}>
                    مسح الفلاتر
                  </Button>
                }
              />
            ) : (
              <div key={tableKey} className="dc-fade-up">
                <OrdersTable
                  orders={filtered}
                  loading={false}
                  flashId={flashId}
                  onOpenOrder={(order) => navigate(`/orders/detail/${order.id}`)}
                  {...handlers}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(rejectOrderId)}
        title="تأكيد رفض الطلب"
        message="هل أنت متأكد من رفض الطلب؟ سيتم إعادة المبلغ للعميل ولا يمكن التراجع عن هذه الخطوة."
        confirmLabel="تأكيد الرفض"
        tone="danger"
        onConfirm={confirmReject}
        onClose={() => setRejectOrderId('')}
      />
    </DashboardLayout>
  );
}